import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { backendFetch, BackendError } from '@/lib/backend';
import { captureAnalyticsEvent } from '@/lib/analytics-client';
import { sanitizeOptionalText, sanitizeText } from '@/lib/sanitize';
import { toast } from 'sonner';

export type FutureSubscription = {
  id: string;
  name: string;
  category: string;
  vendorId?: string;
  planName: string;
  price: number;
  cycle: 'monthly' | 'yearly';
  url: string;
  status: 'considering' | 'researching' | 'trial' | 'waitlisted' | 'rejected' | 'bought' | 'removed';
  intent: 'replace_existing' | 'add_capability' | 'upgrade' | 'bundle' | 'curiosity';
  notes: string;
  priority: number;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type FutureSubInsight = {
  id: string;
  name: string;
  category: string;
  status: string;
  intent: string;
  priority: number;
  price: number;
  cycle: string;
  buyScore: number;
  waitScore: number;
  skipScore: number;
  regretProbability: number;
  overlapScore: number;
  recommendation: string;
  confidence: number;
  overlapCount: number;
  reasoning: Record<string, unknown>;
};

export type FutureSubAnalysis = {
  algorithm_version: string;
  source: string;
  buy_score: number;
  wait_score: number;
  skip_score: number;
  regret_probability: number;
  overlap_score: number;
  stack_compatibility: number;
  productivity_gain: number;
  emotional_risk: number;
  budget_impact: number;
  long_term_value: number;
  cleanliness_impact: number;
  ai_hype_score: number;
  underutilization_risk: number;
  recommendation: string;
  confidence: number;
  reasoning: Record<string, unknown>;
  id: string;
  user_id: string;
  future_subscription_id: string;
  generated_at: string;
};

export type Overlap = {
  future_subscription_id: string;
  subscription_id: string;
  overlap_type: string;
  overlap_score: number;
  shared_features: string[];
  unique_features_future: string[];
  unique_features_existing: string[];
  replacement_viable: boolean;
};

export type FutureSubRecommendation = {
  id: string;
  future_subscription_id: string;
  recommendation: string;
  confidence: number;
  reasoning: Record<string, unknown>;
  replacement_subscription_id: string | null;
  overlap_subscription_ids: string[];
  budget_impact_monthly: number;
  budget_impact_yearly: number;
  projected_roi: number;
};

export type TimelineSimulation = {
  scenario: string;
  current_monthly_spend: number;
  projected_monthly_spend: number;
  projected_yearly_spend: number;
  projected_stack_cleanliness: number;
  projected_density_score: number;
  projected_mood: string;
};

type FutureSubscriptionsResponse = {
  futureSubscriptions?: Record<string, unknown>[];
};

type FutureSubscriptionsInsightsResponse = {
  insights?: FutureSubInsight[];
  activeStackMood?: string | null;
  activeStackCleanliness?: number | null;
};

type FutureSubscriptionAnalysisResponse = {
  analysis: FutureSubAnalysis;
  overlaps: Overlap[];
  recommendation: FutureSubRecommendation;
};

type FutureSubscriptionTimelineResponse = {
  simulations?: TimelineSimulation[];
};

interface FutureSubscriptionContextType {
  futureSubscriptions: FutureSubscription[];
  isLoading: boolean;
  fetchError: string | null;
  entitlementError: { requiredPlanKey: string } | null;
  insights: FutureSubInsight[];
  insightsLoading: boolean;
  activeStackMood: string | null;
  activeStackCleanliness: number | null;
  addFutureSubscription: (sub: FutureSubscription) => Promise<void>;
  updateFutureSubscription: (id: string, updates: Partial<FutureSubscription>) => Promise<void>;
  removeFutureSubscription: (id: string) => Promise<void>;
  fetchAnalysis: (id: string) => Promise<{ analysis: FutureSubAnalysis; overlaps: Overlap[]; recommendation: FutureSubRecommendation } | null>;
  fetchTimeline: (id: string) => Promise<TimelineSimulation[] | null>;
  recordDecision: (id: string, decision: 'buy' | 'wait' | 'skip' | 'replace', rationale?: string) => Promise<void>;
  refreshInsights: () => Promise<void>;
  totalFutureMonthlySpend: number;
  totalFutureYearlySpend: number;
}

const FutureSubscriptionContext = createContext<FutureSubscriptionContextType | undefined>(undefined);

const showFutureMutationFailure = (message: string, error: unknown) => {
  toast.error(message, {
    description: error instanceof Error ? error.message : 'Please try again.',
    duration: 5000,
  });
};

export const FutureSubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const [futureSubscriptions, setFutureSubscriptions] = useState<FutureSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [entitlementError, setEntitlementError] = useState<{ requiredPlanKey: string } | null>(null);
  const [insights, setInsights] = useState<FutureSubInsight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [activeStackMood, setActiveStackMood] = useState<string | null>(null);
  const [activeStackCleanliness, setActiveStackCleanliness] = useState<number | null>(null);
  const hasLoadedOnce = React.useRef(false);

  const normalizeSub = (data: Record<string, unknown>): FutureSubscription => ({
    id: sanitizeText(data.id, 120),
    name: sanitizeText(data.name, 120),
    category: sanitizeText(data.category || 'Software', 80),
    vendorId: sanitizeOptionalText(data.vendorId, 120),
    planName: sanitizeText(data.planName, 120),
    price: Number(data.price || 0),
    cycle: (data.cycle as FutureSubscription['cycle']) || 'monthly',
    url: sanitizeText(data.url, 500),
    status: (data.status as FutureSubscription['status']) || 'considering',
    intent: (data.intent as FutureSubscription['intent']) || 'curiosity',
    notes: sanitizeText(data.notes, 1_000),
    priority: Number(data.priority || 50),
    metadata: typeof data.metadata === 'object' && data.metadata !== null ? data.metadata as Record<string, unknown> : {},
    createdAt: sanitizeText(data.createdAt || data.created_at || '', 40),
    updatedAt: sanitizeText(data.updatedAt || data.updated_at || '', 40),
  });

  const syncSubscriptions = useCallback(async (forceRefresh = false) => {
    if (!user) return;
    try {
      setFetchError(null);
      setEntitlementError(null);
      const data = await backendFetch('/future-subscriptions', { forceRefresh, staleTtlMs: 30_000 }) as FutureSubscriptionsResponse;
      const subs = Array.isArray(data.futureSubscriptions) ? data.futureSubscriptions : [];
      setFutureSubscriptions(subs.map(normalizeSub));
      hasLoadedOnce.current = true;
    } catch (err) {
      if (err instanceof BackendError && err.status === 402 && err.code === 'ENTITLEMENT_REQUIRED') {
        const requiredPlanKey = (err.payload as Record<string, unknown>)?.requiredPlanKey;
        setEntitlementError({ requiredPlanKey: typeof requiredPlanKey === 'string' ? requiredPlanKey : 'core' });
      } else {
        const msg = err instanceof Error ? err.message : 'Unable to load future subscriptions';
        setFetchError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const syncInsights = useCallback(async () => {
    if (!user) return;
    setInsightsLoading(true);
    try {
      const data = await backendFetch('/future-subscriptions-insights', { forceRefresh: true, staleTtlMs: 30_000 }) as FutureSubscriptionsInsightsResponse;
      setInsights(data.insights || []);
      setActiveStackMood(data.activeStackMood || null);
      setActiveStackCleanliness(data.activeStackCleanliness ?? null);
    } catch {
      // insights are supplementary
    } finally {
      setInsightsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setFutureSubscriptions([]);
      setInsights([]);
      setIsLoading(false);
      return;
    }
    syncSubscriptions();
  }, [user, syncSubscriptions]);

  useEffect(() => {
    if (!user || !hasLoadedOnce.current) return;
    syncInsights();
    const interval = setInterval(() => syncInsights(), 120_000);
    return () => clearInterval(interval);
  }, [user, syncInsights, futureSubscriptions.length]);

  const addFutureSubscription = useCallback(async (sub: FutureSubscription) => {
    if (!user) return;
    try {
      await backendFetch('/future-subscriptions', { method: 'POST', body: JSON.stringify(sub) });
      setFutureSubscriptions((prev) => [sub, ...prev.filter((s) => s.id !== sub.id)]);
    } catch (error) {
      showFutureMutationFailure('Unable to add future subscription. Nothing was changed.', error);
      throw error;
    }
    captureAnalyticsEvent('future_subscription_added', { name: sub.name, category: sub.category, intent: sub.intent });
  }, [user]);

  const updateFutureSubscription = useCallback(async (id: string, updates: Partial<FutureSubscription>) => {
    if (!user) return;
    try {
      await backendFetch(`/future-subscriptions/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
      setFutureSubscriptions((prev) => prev.map((s) => s.id === id ? { ...s, ...updates } : s));
    } catch (error) {
      showFutureMutationFailure('Unable to update future subscription. Nothing was changed.', error);
      throw error;
    }
  }, [user]);

  const removeFutureSubscription = useCallback(async (id: string) => {
    if (!user) return;
    try {
      await backendFetch(`/future-subscriptions/${id}`, { method: 'DELETE' });
      setFutureSubscriptions((prev) => prev.filter((s) => s.id !== id));
    } catch (error) {
      showFutureMutationFailure('Unable to remove future subscription. Nothing was changed.', error);
      throw error;
    }
    captureAnalyticsEvent('future_subscription_removed', { id });
  }, [user]);

  const fetchAnalysis = useCallback(async (id: string) => {
    try {
      const data = await backendFetch(`/future-subscriptions/${id}/analysis`, { forceRefresh: true }) as FutureSubscriptionAnalysisResponse;
      return data;
    } catch {
      return null;
    }
  }, []);

  const fetchTimeline = useCallback(async (id: string) => {
    try {
      const data = await backendFetch(`/future-subscriptions/${id}/timeline`, { forceRefresh: true }) as FutureSubscriptionTimelineResponse;
      return data.simulations || null;
    } catch {
      return null;
    }
  }, []);

  const recordDecision = useCallback(async (id: string, decision: 'buy' | 'wait' | 'skip' | 'replace', rationale = '') => {
    if (!user) return;
    try {
      await backendFetch(`/future-subscriptions/${id}/decision`, { method: 'POST', body: JSON.stringify({ decision, rationale }) });
    } catch (error) {
      showFutureMutationFailure('Unable to record decision. Nothing was changed.', error);
      throw error;
    }
    captureAnalyticsEvent('future_subscription_decision', { id, decision });
  }, [user]);

  const refreshInsights = useCallback(async () => {
    await syncInsights();
  }, [syncInsights]);

  const totalFutureMonthlySpend = futureSubscriptions
    .filter((s) => !['removed', 'rejected', 'bought'].includes(s.status))
    .reduce((sum, s) => sum + (s.cycle === 'yearly' ? s.price / 12 : s.price), 0);

  const totalFutureYearlySpend = totalFutureMonthlySpend * 12;

  return (
    <FutureSubscriptionContext.Provider value={{
      futureSubscriptions,
      isLoading,
      fetchError,
      entitlementError,
      insights,
      insightsLoading,
      activeStackMood,
      activeStackCleanliness,
      addFutureSubscription,
      updateFutureSubscription,
      removeFutureSubscription,
      fetchAnalysis,
      fetchTimeline,
      recordDecision,
      refreshInsights,
      totalFutureMonthlySpend,
      totalFutureYearlySpend,
    }}>
      {children}
    </FutureSubscriptionContext.Provider>
  );
};

export const useFutureSubscriptions = () => {
  const context = useContext(FutureSubscriptionContext);
  if (!context) {
    throw new Error('useFutureSubscriptions must be used within a FutureSubscriptionProvider');
  }
  return context;
};
